import React, { useEffect, useRef, useState } from 'react';
import { UserRound } from 'lucide-react';
import { createCoachingSelector, renderCoachingText } from '../services/LearningCoaching';
import MarkdownText from './MarkdownText';
import './LearningCoach.css';

export default function LearningCoach({ scenes = [], trigger, visitKey, variables = {}, actor = null, speaker = null, selectScene, action = null, className = '' }) {
  const selector = useRef(null);
  if (!selector.current) selector.current = createCoachingSelector();
  const selected = (selectScene || selector.current)({ scenes, trigger, visitKey, variables, actor });
  const frame = selected?.frames?.[0];
  const text = renderCoachingText(frame?.text, variables);
  const compact = selected?.meta?.presentation === 'compact';
  const fallbackSpeaker = selected?.meta?.coachingFallback ? speaker : null;
  const name = fallbackSpeaker?.name || frame?.name || '';
  const source = fallbackSpeaker?.image || frame?.imageUrl
    || (/^(https?:\/\/|\/)/.test(frame?.image || '') ? frame.image : '');
  const [failedSource, setFailedSource] = useState(null);
  const imageUrl = frame?.imageUrl;
  // Prepare the selected pose and one next-stage pose, never the entire library.
  useEffect(() => {
    const nextTrigger = /question(?:\.\d+)?$/.test(trigger || '')
      ? trigger.replace(/question(?:\.\d+)?$/, 'answer') : /answer$/.test(trigger || '') ? trigger.replace(/answer$/, 'repeat') : null;
    const next = scenes.find(scene => scene.trigger === nextTrigger && !scene.meta?.coachingFallback
      && (!actor || scene.frames?.[0]?.actor === actor || nextTrigger?.endsWith('.repeat')));
    const urls = new Set([imageUrl, next?.frames?.[0]?.imageUrl].filter(Boolean));
    const images = [...urls].map(url => { const image = new Image(); image.src = url; return image; });
    return () => { images.forEach(image => { image.onload = null; image.onerror = null; }); };
  }, [scenes, trigger, actor, imageUrl]);
  if (!selected || text === null) return null;
  return <div className={`learning-coach is-${compact ? 'compact' : 'default'} ${className}`.trim()} data-coaching-id={selected.id}>
    {source && failedSource !== source
      ? <img className="learning-coach-portrait" src={source} alt={name} onError={() => setFailedSource(source)} />
      : <span className="learning-coach-portrait learning-coach-placeholder" role="img" aria-label={name}><UserRound size={compact ? 24 : 40} aria-hidden="true" /></span>}
    <div className="learning-coach-bubble" aria-live="polite" aria-atomic="true">
      {!compact && name && <strong className="learning-coach-name">{name}</strong>}
      <p><MarkdownText text={text} /></p>
    </div>
    {action && <div className="learning-coach-action">{action}</div>}
  </div>;
}
